import type {
	ConnectedForm,
	Attempt,
	BingoPattern,
	CompletedReward,
	CompletedResult,
	FormInput,
	GameConfig,
	GameType,
	Language,
	LeaderboardRow,
	NewTriviaSettings,
	Player,
	Question,
	QuestionOption,
	QuestionType,
	SelectorMode,
	Theme,
	ValidationResult,
} from './types';

type RawRecord = Record<string, unknown>;

const asRecord = (value: unknown): RawRecord =>
	typeof value === 'object' && value !== null ? value as RawRecord : {};
const asString = (value: unknown, fallback = ''): string =>
	typeof value === 'string' ? value : value == null ? fallback : String(value);
const asNumber = (value: unknown, fallback = 0): number => {
	const number = Number(value);
	return Number.isFinite(number) ? number : fallback;
};
const asBoolean = (value: unknown): boolean =>
	value === true || value === 1 || value === '1' || value === 'true';
const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : [];

const gameTypes: GameType[] = ['quiz', 'categoryClash', 'bingo'];
const questionTypes: QuestionType[] = ['mc4', 'tf', 'gw', 'ro', 'ms', 'pa', 'vmc4'];
const languages: Language[] = ['en', 'es', 'fr'];
const selectors: SelectorMode[] = ['cards', 'wheel', 'slot'];
const bingoPatterns: BingoPattern[] = ['row', 'column', 'diagonal', 'fourCorners', 'fullBoard'];

const enumValue = <T extends string>(value: unknown, values: T[], fallback: T): T =>
	values.includes(value as T) ? value as T : fallback;

const adaptOption = (raw: unknown): QuestionOption => {
	const option = asRecord(raw);
	return {
		id: asString(option.id).toLowerCase(),
		text: asString(option.text),
		imageUrl: asString(option.imageUrl),
	};
};

const adaptQuestion = (raw: unknown): Question => {
	const question = asRecord(raw);
	return {
		id: asNumber(question.id),
		type: enumValue(question.type, questionTypes, 'mc4'),
		category: asString(question.category),
		points: Math.min(1000, Math.max(0, asNumber(question.points, 100))),
		ordinal: asNumber(question.ordinal),
		text: asString(question.text),
		hint: asString(question.hint),
		explanation: asString(question.explanation),
		imageUrl: asString(question.imageUrl),
		videoUrl: asString(question.videoUrl),
		videoRevealDurationSeconds: Math.min(300, Math.max(0, asNumber(question.videoRevealDurationSeconds, 20))),
		answerLength: Math.floor(Math.min(500, Math.max(0, asNumber(question.answerLength)))),
		options: asArray(question.options).map(adaptOption),
	};
};

const adaptFormInput = (raw: unknown): FormInput => {
	const input = asRecord(raw);
	return {
		id: asNumber(input.id),
		type: asString(input.type, 'text'),
		label: asString(input.label),
		name: asString(input.name),
		title: asString(input.title),
		detail: asString(input.detail),
		isRequired: asNumber(input.isRequired) > 0,
		minimumSelections: input.type === 'multi-select' ? Math.max(0, asNumber(input.isRequired)) : 0,
		options: asArray(input.options).map((option) => asString(option)),
	};
};

const adaptForm = (raw: unknown, useStableSurveyKeys = false): ConnectedForm | null => {
	if (!raw) return null;
	const form = asRecord(raw);
	const inputs = asArray(form.inputs).map(adaptFormInput);
	if (useStableSurveyKeys) {
		for (const input of inputs) input.name = `surveyInput_${input.id}`;
	}
	return {
		id: asNumber(form.id),
		title: asString(form.title),
		description: asString(form.description),
		inputs,
	};
};

const color = (value: unknown, fallback: string): string => {
	const candidate = asString(value).trim();
	const hex = candidate.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
	if (hex) return `rgba(${parseInt(hex[1], 16)},${parseInt(hex[2], 16)},${parseInt(hex[3], 16)},1)`;
	const match = candidate.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1(?:\.0+)?|0?\.\d+))?\s*\)$/i);
	if (!match) return color(fallback, 'rgba(0,0,0,1)');
	const channels = match.slice(1, 4).map(Number);
	const alpha = match[4] === undefined ? 1 : Number(match[4]);
	return channels.every((channel) => channel <= 255) && alpha >= 0 && alpha <= 1
		? `rgba(${channels[0]},${channels[1]},${channels[2]},${alpha})`
		: color(fallback, 'rgba(0,0,0,1)');
};

const adaptTheme = (raw: unknown): Theme => {
	const theme = asRecord(raw);
	const engineVersion = Math.max(0, Math.floor(asNumber(theme.engineVersion)));
	const personality = enumValue(theme.personality, ['playful', 'corporate', 'warm', 'cool'], 'playful');
	const buttonStyle = enumValue<Theme['buttonStyle']>(theme.buttonStyle, ['classic', 'rounded', 'square', 'custom'], 'rounded');
	const buttonStyleValues: Partial<Record<Theme['buttonStyle'], { radius: number; border: number }>> = {
		classic: { radius: 16, border: 1 },
		rounded: { radius: 30, border: 1 },
		square: { radius: 2, border: 1 },
	};
	const useResolvedButtonValues = engineVersion === 2 || buttonStyle === 'custom';
	const defaultCategoryPalette = ['#2458D8', '#2458D8', '#2DBFCC', '#F0A21F', '#C83D4D'];
	const categoryPaletteValues = asArray(theme.categoryPalette);
	const categoryPalette = categoryPaletteValues.length
		? categoryPaletteValues.map((value, index) => color(value, defaultCategoryPalette[index % defaultCategoryPalette.length]))
		: [1, 2, 3, 4, 5].map((index) => color(theme[`categoryPalette${index}`], defaultCategoryPalette[index - 1]));
	return {
		engineVersion,
		mode: theme.mode === 'dark' ? 'dark' : 'light',
		personality,
		primaryColor: color(theme.primaryColor, '#2458D8'),
		secondaryColor: color(theme.secondaryColor, '#6E43E8'),
		accentColor: color(theme.accentColor, '#F0A21F'),
		backgroundColor: color(theme.backgroundColor, '#F4F7FF'),
		textColor: color(theme.textColor, '#123F8C'),
		successColor: color(theme.successColor, '#16A86A'),
		errorColor: color(theme.errorColor, '#C83D4D'),
		fontFamily: asString(theme.fontFamily, 'Helvetica'),
		secondaryFontFamily: asString(theme.secondaryFontFamily, 'Helvetica'),
		buttonFontFamily: asString(theme.buttonFontFamily, 'Helvetica'),
		leaderboardFontFamily: asString(theme.leaderboardFontFamily, 'Helvetica'),
		buttonStyle,
		buttonRadius: Math.min(50, Math.max(0, useResolvedButtonValues
			? asNumber(theme.buttonRadius, buttonStyleValues[buttonStyle]?.radius ?? 30)
			: buttonStyleValues[buttonStyle]?.radius ?? 30)),
		inputRadius: Math.min(50, Math.max(0, asNumber(theme.inputRadius, 8))),
		buttonBorderWidth: Math.min(8, Math.max(0, useResolvedButtonValues
			? asNumber(theme.buttonBorderWidth, buttonStyleValues[buttonStyle]?.border ?? 1)
			: buttonStyleValues[buttonStyle]?.border ?? 1)),
		cardRadius: Math.min(50, Math.max(0, asNumber(theme.cardRadius, 18))),
		heroRadius: Math.min(50, Math.max(0, asNumber(theme.heroRadius, asNumber(theme.cardRadius, 18)))),
		topLogoUrl: asString(theme.topLogoUrl) || asString(theme.logoUrl),
		gameLogoUrl: asString(theme.gameLogoUrl),
		backgroundImageUrl: asString(theme.backgroundImageUrl),
		appBackground: color(theme.appBackground, color(theme.backgroundColor, '#F4F7FF')),
		surfaceBackground: color(theme.surfaceBackground, '#FFFFFF'),
		secondarySurfaceBackground: color(theme.secondarySurfaceBackground, '#F7F9FF'),
		elevatedSurface: color(theme.elevatedSurface, color(theme.surfaceBackground, '#FFFFFF')),
		inputBackground: color(theme.inputBackground, color(theme.secondarySurfaceBackground, '#F7F9FF')),
		cardBackground: color(theme.cardBackground, color(theme.secondarySurfaceBackground, '#F7F9FF')),
		borderColor: color(theme.borderColor, '#D6DFF5'),
		inputBorder: color(theme.inputBorder, color(theme.borderColor, '#D6DFF5')),
		divider: color(theme.divider, color(theme.borderColor, '#D6DFF5')),
		inputText: color(theme.inputText, color(theme.textColor, '#123F8C')),
		mutedTextColor: color(theme.mutedTextColor, '#5B6780'),
		primaryText: color(theme.primaryText, color(theme.textColor, '#123F8C')),
		secondaryText: color(theme.secondaryText, color(theme.mutedTextColor, '#5B6780')),
		placeholderText: color(theme.placeholderText, color(theme.mutedTextColor, '#5B6780')),
		focusGlow: color(theme.focusGlow, 'rgba(36,88,216,0.24)'),
		linkHoverColor: color(theme.linkHoverColor, color(theme.secondaryColor, '#6E43E8')),
		headerBackground: color(theme.headerBackground, color(theme.surfaceBackground, '#FFFFFF')),
		pillBackground: color(theme.pillBackground, color(theme.primaryColor, '#2458D8')),
		pillText: color(theme.pillText, '#FFFFFF'),
		pillBorder: color(theme.pillBorder, color(theme.secondaryColor, '#6E43E8')),
		loginCardBackground: color(theme.loginCardBackground, '#FFFFFF'),
		loginTextColor: color(theme.loginTextColor, '#123F8C'),
		loginLinkColor: color(theme.loginLinkColor, '#6E43E8'),
		heroTitleColor: color(theme.heroTitleColor, color(theme.primaryButtonText, '#FFFFFF')),
		heroSecondaryTextColor: color(theme.heroSecondaryTextColor, color(theme.primaryButtonText, '#FFFFFF')),
		heroScrimColor: color(theme.heroScrimColor, 'rgba(0,0,0,0)'),
		primaryButtonBackground: color(theme.primaryButtonBackground, '#2458D8'),
		primaryButtonText: color(theme.primaryButtonText, '#FFFFFF'),
		primaryButtonHover: color(theme.primaryButtonHover, color(theme.primaryButtonBackground, '#2458D8')),
		primaryButtonBorder: color(theme.primaryButtonBorder, color(theme.primaryButtonBackground, '#2458D8')),
		primaryButtonActiveShadow: color(theme.primaryButtonActiveShadow, 'rgba(0,0,0,0.25)'),
		primaryButtonGradient: asString(theme.primaryButtonGradient, 'none'),
		secondaryButtonBackground: color(theme.secondaryButtonBackground, color(theme.secondarySurfaceBackground, '#F7F9FF')),
		secondaryButtonText: color(theme.secondaryButtonText, color(theme.textColor, '#123F8C')),
		secondaryButtonBorder: color(theme.secondaryButtonBorder, color(theme.borderColor, '#D6DFF5')),
		secondaryButtonHover: color(theme.secondaryButtonHover, color(theme.secondaryButtonBackground, '#F7F9FF')),
		disabledOpacity: Math.min(1, Math.max(0, asNumber(theme.disabledOpacity, .55))),
		allowCardGradients: asBoolean(theme.allowCardGradients),
		cardGradientEnd: color(theme.cardGradientEnd, color(theme.textColor, '#123F8C')),
		heroGradientEnd: color(theme.heroGradientEnd, color(theme.secondaryColor, '#6E43E8')),
		heroGradientAngle: enumValue(theme.heroGradientAngle, ['120deg', '145deg', '160deg'], '145deg'),
		shadowColor: color(theme.shadowColor, 'rgba(0,0,0,0.25)'),
		softShadowColor: color(theme.softShadowColor, 'rgba(0,0,0,0.08)'),
		inputShadow: color(theme.inputShadow, color(theme.softShadowColor, 'rgba(0,0,0,0.08)')),
		shadowBlurMultiplier: Math.min(3, Math.max(.25, asNumber(theme.shadowBlurMultiplier, 1))),
		shadowDepthMultiplier: Math.min(3, Math.max(.25, asNumber(theme.shadowDepthMultiplier, 1))),
		overlayColor: color(theme.overlayColor, 'rgba(18,63,140,0.42)'),
		questionPanelBackground: color(theme.questionPanelBackground, '#FFFFFF'),
		questionImageBackground: color(theme.questionImageBackground, color(theme.elevatedSurface, '#FFFFFF')),
		unselectedBackground: color(theme.unselectedBackground, color(theme.inputBackground, '#F7F9FF')),
		unselectedText: color(theme.unselectedText, color(theme.textColor, '#123F8C')),
		unselectedBorder: color(theme.unselectedBorder, color(theme.borderColor, '#D6DFF5')),
		unselectedGlow: color(theme.unselectedGlow, color(theme.inputShadow, 'rgba(0,0,0,0.08)')),
		answerLetterUnselectedBackground: color(theme.answerLetterUnselectedBackground, color(theme.inputBackground, '#FFFFFF')),
		answerLetterUnselectedText: color(theme.answerLetterUnselectedText, color(theme.inputText, '#123F8C')),
		answerLetterUnselectedBorder: color(theme.answerLetterUnselectedBorder, color(theme.inputBorder, '#D6DFF5')),
		answerSelectedBackground: color(theme.answerSelectedBackground, '#EEE9FF'),
		answerCorrectBackground: color(theme.answerCorrectBackground, '#16A86A'),
		answerIncorrectBackground: color(theme.answerIncorrectBackground, '#C83D4D'),
		answerLetterSelectedBackground: color(theme.answerLetterSelectedBackground, color(theme.primaryColor, '#2458D8')),
		answerLetterSelectedText: color(theme.answerLetterSelectedText, '#FFFFFF'),
		answerLetterSelectedBorder: color(theme.answerLetterSelectedBorder, color(theme.selectedBorder, color(theme.primaryColor, '#2458D8'))),
		selectedText: color(theme.selectedText, color(theme.textColor, '#123F8C')),
		selectedBorder: color(theme.selectedBorder, color(theme.primaryColor, '#2458D8')),
		selectedGlow: color(theme.selectedGlow, 'rgba(36,88,216,0.24)'),
		selectedBackground: color(theme.selectedBackground, color(theme.answerSelectedBackground, '#EEE9FF')),
		answerLetterCorrectBackground: color(theme.answerLetterCorrectBackground, color(theme.answerCorrectBackground, '#16A86A')),
		answerLetterCorrectText: color(theme.answerLetterCorrectText, '#FFFFFF'),
		answerLetterCorrectBorder: color(theme.answerLetterCorrectBorder, color(theme.correctBorder, color(theme.answerCorrectBackground, '#16A86A'))),
		correctText: color(theme.correctText, color(theme.textColor, '#123F8C')),
		correctBorder: color(theme.correctBorder, color(theme.answerCorrectBackground, '#16A86A')),
		correctGlow: color(theme.correctGlow, 'rgba(22,168,106,0.24)'),
		correctBackground: color(theme.correctBackground, color(theme.answerCorrectBackground, '#16A86A')),
		answerLetterIncorrectBackground: color(theme.answerLetterIncorrectBackground, color(theme.answerIncorrectBackground, '#C83D4D')),
		answerLetterIncorrectText: color(theme.answerLetterIncorrectText, '#FFFFFF'),
		answerLetterIncorrectBorder: color(theme.answerLetterIncorrectBorder, color(theme.incorrectBorder, color(theme.answerIncorrectBackground, '#C83D4D'))),
		incorrectText: color(theme.incorrectText, color(theme.textColor, '#123F8C')),
		incorrectBorder: color(theme.incorrectBorder, color(theme.answerIncorrectBackground, '#C83D4D')),
		incorrectGlow: color(theme.incorrectGlow, 'rgba(200,61,77,0.24)'),
		incorrectBackground: color(theme.incorrectBackground, color(theme.answerIncorrectBackground, '#C83D4D')),
		questionFeedbackIncorrectBackground: color(theme.questionFeedbackIncorrectBackground, color(theme.incorrectBackground, '#FCE8EB')),
		questionFeedbackIncorrectBorder: color(theme.questionFeedbackIncorrectBorder, color(theme.incorrectBorder, '#C83D4D')),
		questionFeedbackIncorrectText: color(theme.questionFeedbackIncorrectText, color(theme.incorrectText, '#C83D4D')),
		categoryPalette,
		categoryLabelBackground: color(theme.categoryLabelBackground, '#2458D8'),
		categoryLabelText: color(theme.categoryLabelText, '#FFFFFF'),
		categoryClashBoardTitleColor: color(theme.categoryClashBoardTitleColor, color(theme.textColor, '#123F8C')),
		categoryClashQuestionsCompletedLabelColor: color(theme.categoryClashQuestionsCompletedLabelColor, color(theme.mutedTextColor, '#5B6780')),
		categoryClashBoardBackground: color(theme.categoryClashBoardBackground, '#123F8C'),
		categoryClashCategoryBackground: color(theme.categoryClashCategoryBackground, '#2458D8'),
		categoryClashCategoryBorder: color(theme.categoryClashCategoryBorder, color(theme.borderColor, '#D6DFF5')),
		categoryClashCategoryText: color(theme.categoryClashCategoryText, '#FFFFFF'),
		categoryClashTileBackground: color(theme.categoryClashTileBackground, '#FFFFFF'),
		categoryClashTileText: color(theme.categoryClashTileText, '#123F8C'),
		categoryClashHeaderStatsBackground: color(theme.categoryClashHeaderStatsBackground, '#FFFFFF'),
		categoryClashHeaderStatsBorder: color(theme.categoryClashHeaderStatsBorder, '#D6DFF5'),
		categoryClashHeaderStatsText: color(theme.categoryClashHeaderStatsText, '#123F8C'),
		bingoBoardBackground: color(theme.bingoBoardBackground, '#FFFFFF'),
		bingoBoardBorder: color(theme.bingoBoardBorder, '#E6E6E6'),
		bingoTileBackground: color(theme.bingoTileBackground, '#F7F9FF'),
		bingoTileText: color(theme.bingoTileText, '#123F8C'),
		bingoLineColor: color(theme.bingoLineColor, '#6E43E8'),
		finalCardBackground: color(theme.finalCardBackground, '#FFFFFF'),
		finalScoreColor: color(theme.finalScoreColor, '#2458D8'),
		finalActionBackground: color(theme.finalActionBackground, '#2458D8'),
		finalActionText: color(theme.finalActionText, '#FFFFFF'),
		summaryItemBackground: color(theme.summaryItemBackground, color(theme.secondarySurfaceBackground, '#F7F9FF')),
		summaryItemLabelText: color(theme.summaryItemLabelText, color(theme.mutedTextColor, '#5B6780')),
		summaryItemBorder: color(theme.summaryItemBorder, color(theme.borderColor, '#D6DFF5')),
		leaderboardBackground: color(theme.leaderboardBackground, '#FFFFFF'),
		leaderboardTextColor: color(theme.leaderboardTextColor, '#123F8C'),
		leaderboardRowBackground: color(theme.leaderboardRowBackground, color(theme.secondarySurfaceBackground, '#F7F9FF')),
		leaderboardRowText: color(theme.leaderboardRowText, color(theme.textColor, '#123F8C')),
		leaderboardRowBorder: color(theme.leaderboardRowBorder, color(theme.borderColor, '#D6DFF5')),
		leaderboardYouBackground: color(theme.leaderboardYouBackground, color(theme.answerSelectedBackground, '#EEE9FF')),
		leaderboardYouText: color(theme.leaderboardYouText, color(theme.textColor, '#123F8C')),
		leaderboardYouBorder: color(theme.leaderboardYouBorder, color(theme.selectedBorder, '#2458D8')),
	};
};

const adaptSettings = (raw: unknown, homeButtonBackgroundFallback = '#F7F9FF', cardColorFallback = '#2458D8'): NewTriviaSettings => {
	const settings = asRecord(raw);
	const scoring = asRecord(settings.scoring);
	const replay = asRecord(settings.replay);
	const gameOver = asRecord(settings.gameOver);
	const completion = asRecord(settings.completion);
	const selectorSettings = asRecord(settings.categorySelectorSettings);
	const storedDurationSeconds = Math.min(300, Math.max(0, asNumber(scoring.durationSeconds, 30)));
	const timerEnabled = asBoolean(scoring.timerEnabled) && storedDurationSeconds > 0;
	const durationSeconds = timerEnabled ? storedDurationSeconds : 0;
	const adaptedBingoPatterns = asArray(scoring.bingoWinningPatterns)
		.map((pattern) => asString(pattern))
		.filter((pattern): pattern is BingoPattern => bingoPatterns.includes(pattern as BingoPattern));
	return {
		schemaVersion: asNumber(settings.schemaVersion, 1),
		gameType: enumValue(settings.gameType, gameTypes, 'quiz'),
		language: enumValue(settings.language, languages, 'en'),
		loginMode: settings.loginMode === 'leadCapture' ? 'leadCapture' : 'name',
		playerNameLabel: asString(settings.playerNameLabel, 'Please enter your name'),
		surveyWindowLabel: asString(settings.surveyWindowLabel, 'Survey'),
		surveyButtonLabel: asString(settings.surveyButtonLabel, 'Continue'),
		showConfirmation: asBoolean(settings.showConfirmation),
		showSurvey: asBoolean(settings.showSurvey),
		kioskMode: asBoolean(settings.kioskMode),
		buttonHomeIcon: /^fa-[a-z0-9-]+$/.test(asString(settings.buttonHomeIcon)) ? asString(settings.buttonHomeIcon) : 'fa-home',
		buttonHomeIconColor: color(settings.buttonHomeIconColor, '#2458D8'),
		buttonHomeBackgroundColor: color(settings.buttonHomeBackgroundColor, homeButtonBackgroundFallback),
		domainRestrictionMode: settings.domainRestrictionMode === 'block' ? 'block' : 'allow',
		domainRestrictionsEnabled: asBoolean(settings.domainRestrictionsEnabled),
		allowedDomains: asArray(settings.allowedDomains).map((domain) => asString(domain)),
		footerEnabled: asBoolean(settings.footerEnabled),
		footerText: asString(settings.footerText),
		footerLinkLabel: asString(settings.footerLinkLabel),
		footerUrl: asString(settings.footerUrl),
		customInstructionsEnabled: asBoolean(settings.customInstructionsEnabled),
		showInstructionsScreen: Object.prototype.hasOwnProperty.call(settings, 'showInstructionsScreen') ? asBoolean(settings.showInstructionsScreen) : asBoolean(settings.customInstructionsEnabled),
		instructionsTitle: asString(settings.instructionsTitle, 'How to play'),
		instructionsText: asString(settings.instructionsText),
		startButtonLabel: asString(settings.startButtonLabel, 'Start Game'),
		bingoTitle: asString(settings.bingoTitle, 'Trivia Bingo'),
		bingoDescription: asString(settings.bingoDescription, 'Choose a square. Correct answers mark the board.'),
		categoryClashBoardTitle: asString(settings.categoryClashBoardTitle, 'Choose a Question'),
		categoryClashQuestionsCompletedLabel: asString(settings.categoryClashQuestionsCompletedLabel, 'Questions Completed'),
		categoryClashScoreLabel: asString(settings.categoryClashScoreLabel, 'Score'),
		categoryClashButtonLabel: asString(settings.categoryClashButtonLabel, 'Submit Score & End Game'),
		bingoSubmitButtonLabel: asString(settings.bingoSubmitButtonLabel, 'Submit Bingo'),
		bingoAnsweredLabel: asString(settings.bingoAnsweredLabel, 'Answered'),
		bingoCorrectLabel: asString(settings.bingoCorrectLabel, 'Correct'),
		bingoScoreLabel: asString(settings.bingoScoreLabel, 'Score'),
		bingoBonusLabel: asString(settings.bingoBonusLabel, 'Bonus Points'),
		bingoBonusPointsLabel: asString(settings.bingoBonusPointsLabel, 'Bonus:'),
		bingoBonusPointsDescription: asString(settings.bingoBonusPointsDescription, 'per winning pattern'),
		questionCount: Math.min(500, Math.max(1, asNumber(settings.questionCount, 6))),
		questionFlow: settings.questionFlow === 'random' ? 'random' : 'ordinal',
		shuffleQuestions: asBoolean(settings.shuffleQuestions),
		showCorrectResponses: asBoolean(settings.showCorrectResponses),
		playSounds: asBoolean(settings.playSounds),
		showCategories: asBoolean(settings.showCategories),
		categorySelector: enumValue(settings.categorySelector, selectors, 'cards'),
		categorySelectorSettings: {
			cardColor: color(selectorSettings.cardColor, cardColorFallback),
			cardImageUrl: asString(selectorSettings.cardImageUrl),
			wheelColors: asArray(selectorSettings.wheelColors).map((value) => color(value, '')).filter(Boolean),
			wheelFontColor: color(selectorSettings.wheelFontColor, '#FFFFFF'),
			slotFontColor: color(selectorSettings.slotFontColor, '#FFFFFF'),
			slotBarrelColor: color(selectorSettings.slotBarrelColor, '#0B3152'),
			slotMachineColor: color(selectorSettings.slotMachineColor, '#2DBFCC'),
		},
		scoring: {
			mode: scoring.mode === 'speed' ? 'speed' : 'fixed',
			basePoints: Math.min(1000, Math.max(0, asNumber(scoring.basePoints, 450))),
			timerEnabled,
			durationSeconds,
			streakEnabled: asBoolean(scoring.streakEnabled),
			streakMultiplierStep: Math.min(10, Math.max(1, asNumber(scoring.streakMultiplierStep, 1))),
			bingoWinningPatterns: adaptedBingoPatterns.length ? adaptedBingoPatterns : ['row', 'column', 'diagonal'],
			bingoCorrectAnswerPoints: Math.min(10000, Math.max(0, asNumber(scoring.bingoCorrectAnswerPoints, 10))),
			bingoBonusEnabled: Object.prototype.hasOwnProperty.call(scoring, 'bingoBonusEnabled') ? asBoolean(scoring.bingoBonusEnabled) : true,
			bingoPatternBonusPoints: Math.min(10000, Math.max(0, asNumber(scoring.bingoPatternBonusPoints, 50))),
			bingoFullBoardBonusEnabled: asBoolean(scoring.bingoFullBoardBonusEnabled),
			bingoFullBoardBonusPoints: Math.min(10000, Math.max(0, asNumber(scoring.bingoFullBoardBonusPoints, 100))),
			bingoBonusTitle: asString(scoring.bingoBonusTitle, 'Bingo!'),
			bingoBonusCopy: asString(scoring.bingoBonusCopy, 'You completed the {pattern} for +{points} bonus points.'),
		},
		replay: {
			enabled: asBoolean(replay.enabled),
			limitEnabled: asBoolean(replay.limitEnabled),
			maxPlays: Math.max(1, asNumber(replay.maxPlays, 1)),
		},
		gameOver: {
			title: asString(gameOver.title, 'Game Over'),
			description: asString(gameOver.description, 'This game is now closed.  We hope you had fun'),
		},
		completion: {
			title: asString(completion.title, 'Nice work!'),
			message: asString(completion.message),
			showScore: asBoolean(completion.showScore),
			showLeaderboard: asBoolean(completion.showLeaderboard),
			leaderboardPreviewStyle: enumValue(completion.leaderboardPreviewStyle, ['top5', 'top10', 'myRankTop5'], 'top5'),
			primaryLabel: asString(completion.primaryLabel, 'View leaderboard'),
			primaryUrl: asString(completion.primaryUrl),
			secondaryLabel: asString(completion.secondaryLabel, 'Done'),
			secondaryUrl: asString(completion.secondaryUrl),
			exitBehavior: enumValue(completion.exitBehavior, ['login', 'leaderboard', 'url'], 'login'),
		},
	};
};

export const adaptGameConfig = (raw: unknown): GameConfig => {
	const game = asRecord(raw);
	const event = asRecord(game.event);
	const theme = adaptTheme(game.theme);
	return {
		id: asNumber(game.id),
		eventId: asString(game.eventId),
		name: asString(game.name),
		title: asString(game.title),
		description: asString(game.description),
		gameOverText: asString(game.gameOverText),
		notifyEmail: asString(game.notifyEmail),
		notifyUrl: asString(game.notifyUrl),
		event: {
			id: asString(event.id),
			title: asString(event.title),
			slug: asString(event.slug),
			active: asBoolean(event.active),
		},
		settings: adaptSettings(game.settings, theme.secondarySurfaceBackground, theme.primaryColor),
		theme,
		questions: asArray(game.questions).map(adaptQuestion),
		leadCaptureForm: adaptForm(game.leadCaptureForm),
		surveyForm: adaptForm(game.surveyForm, true),
		hasActiveResultSet: asBoolean(game.hasActiveResultSet),
		playable: asBoolean(game.playable),
		validationErrors: asArray(game.validationErrors).map((error) => asString(error)),
	};
};

export const adaptPlayer = (raw: unknown): Player => {
	const player = asRecord(raw);
	return {
		id: asNumber(player.id),
		fullName: asString(player.fullName),
		firstName: asString(player.firstName),
		lastName: asString(player.lastName),
		email: asString(player.email),
		leadId: player.leadId == null || player.leadId === '' ? null : asNumber(player.leadId),
	};
};

export const adaptAttempt = (raw: unknown): Attempt => {
	const attempt = asRecord(raw);
	return {
		id: asNumber(attempt.id),
		playerId: asNumber(attempt.playerId),
		rsid: asNumber(attempt.rsid),
		attemptNumber: Math.max(1, asNumber(attempt.attemptNumber, 1)),
		startedAt: asString(attempt.startedAt),
		questions: asArray(attempt.questions).map((rawQuestion) => {
			const question = asRecord(rawQuestion);
			const questionSnapshot = asRecord(question.question);
			return {
				questionId: asNumber(question.questionId),
				boardIndex: Math.max(0, asNumber(question.boardIndex)),
				points: Math.max(0, asNumber(question.points)),
				question: Object.keys(questionSnapshot).length ? adaptQuestion(questionSnapshot) : undefined,
			};
		}).sort((a, b) => a.boardIndex - b.boardIndex),
	};
};

export const adaptValidationResult = (raw: unknown): ValidationResult => {
	const validation = asRecord(raw);
	return {
		questionId: asNumber(validation.questionId),
		correct: asBoolean(validation.correct),
		correctResponse: validation.correctResponse == null ? null : asString(validation.correctResponse),
		explanation: asString(validation.explanation),
		award: asNumber(validation.award),
		score: validation.score == null ? null : asNumber(validation.score),
	};
};

const adaptCompletedReward = (raw: unknown): CompletedReward | null => {
	if (typeof raw !== 'object' || raw === null) return null;
	const reward = asRecord(raw);
	const popup = asRecord(reward.popup);
	const prizeValue = typeof reward.prize === 'object' && reward.prize !== null ? asRecord(reward.prize) : null;
	return {
		awardId: Math.max(0, asNumber(reward.awardId)),
		type: enumValue(reward.type, ['instantWin', 'raffleWheel', 'namePicker'], 'instantWin'),
		popup: {
			enabled: asBoolean(popup.enabled),
			title: asString(popup.title),
			message: asString(popup.message),
			buttonLabel: asString(popup.buttonLabel, 'Dismiss'),
			imageUrl: asString(popup.imageUrl),
			backgroundColor: asString(popup.backgroundColor, '#FFFFFF'),
			fontColor: asString(popup.fontColor, '#222222'),
		},
		prize: prizeValue ? {
			name: asString(prizeValue.name),
			description: asString(prizeValue.description),
			imageUrl: asString(prizeValue.imageUrl),
		} : null,
	};
};

export const adaptCompletedResult = (raw: unknown): CompletedResult => {
	const result = asRecord(raw);
	return {
		id: asNumber(result.id),
		attemptId: asNumber(result.attemptId),
		gameId: asNumber(result.gameId),
		playerId: asNumber(result.playerId),
		gameType: enumValue(result.gameType, gameTypes, 'quiz'),
		attemptNumber: Math.max(1, asNumber(result.attemptNumber, 1)),
		finalScore: asNumber(result.finalScore),
		correctAnswers: Math.max(0, asNumber(result.correctAnswers)),
		questionsAnswered: Math.max(0, asNumber(result.questionsAnswered)),
		completionTimeMs: Math.max(0, asNumber(result.completionTimeMs)),
		completionReason: enumValue(result.completionReason, ['completed', 'early_end', 'bingo_submit'], 'completed'),
		completedAt: asString(result.completedAt),
		reward: adaptCompletedReward(result.reward),
	};
};

export const adaptLeaderboardRow = (raw: unknown): LeaderboardRow => {
	const row = asRecord(raw);
	return {
		rank: Math.max(1, asNumber(row.rank, 1)),
		playerId: asNumber(row.playerId),
		name: asString(row.name, 'Player'),
		score: asNumber(row.score),
		correctAnswers: Math.max(0, asNumber(row.correctAnswers)),
		questionsAnswered: Math.max(0, asNumber(row.questionsAnswered)),
		completionTimeMs: Math.max(0, asNumber(row.completionTimeMs)),
		completedAt: asString(row.completedAt),
	};
};
